import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, CircularProgress, Chip, Avatar,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Snackbar, Alert, Tooltip, TextField, Pagination, Stack
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ProductService, PendingProduct, ProductError } from '../services/ProductService';
import { CACHE_KEYS } from '../services/cache/cacheKeys';
import { CACHE_TTL, dataCache } from '../services/cache/dataCache';
import { useCachedQuery } from '../hooks/useCachedQuery';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import DataTableCard from '../components/ui/DataTableCard';

const PAGE_SIZE = 20;

const PendingProducts: React.FC = () => {
  const fetchPendingProducts = useCallback(() => ProductService.getPendingProducts(), []);

  const { data: products, loading, refetch } = useCachedQuery(
    CACHE_KEYS.pendingProducts,
    fetchPendingProducts,
    { ttlMs: CACHE_TTL.pendingProducts }
  );

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; productId?: string; productName?: string }>({
    open: false
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [editDialog, setEditDialog] = useState<{ open: boolean, product?: PendingProduct }>({ open: false });
  const [editUrunAdi, setEditUrunAdi] = useState('');
  const [editBarkod, setEditBarkod] = useState('');
  const [approving, setApproving] = useState(false);
  const [page, setPage] = useState(1);
  const [gotoPage, setGotoPage] = useState('');
  const [gotoError, setGotoError] = useState('');

  const productList = useMemo(() => products ?? [], [products]);
  const totalPages = Math.max(1, Math.ceil(productList.length / PAGE_SIZE));

  const paginatedProducts = useMemo(
    () => productList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [productList, page]
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGotoPage = () => {
    const num = Number(gotoPage);
    if (!num || num < 1 || num > totalPages) {
      setGotoError(`1 - ${totalPages} arasında bir sayfa girin`);
      return;
    }
    setPage(num);
    setGotoError('');
    setGotoPage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGotoInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleGotoPage();
  };

  const handleDeleteClick = (productId: string, productName: string) => {
    setDeleteDialog({ open: true, productId, productName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.productId) return;

    try {
      const success = await ProductService.deletePendingProduct(deleteDialog.productId);
      if (success) {
        const updated = productList.filter((p) => p.id !== deleteDialog.productId);
        dataCache.set(CACHE_KEYS.pendingProducts, updated);
        const newTotalPages = Math.max(1, Math.ceil(updated.length / PAGE_SIZE));
        if (page > newTotalPages) setPage(newTotalPages);
        await refetch(false);
        showSnackbar('Ürün başarıyla silindi', 'success');
      } else {
        showSnackbar('Ürün silinirken bir hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      showSnackbar('Ürün silinirken bir hata oluştu', 'error');
    } finally {
      setDeleteDialog({ open: false });
    }
  };

  const handleCopyBarkod = async (barkod: string) => {
    try {
      await navigator.clipboard.writeText(barkod);
      showSnackbar('Barkod kopyalandı', 'success');
    } catch (error) {
      console.error('Barkod kopyalanırken hata:', error);
      showSnackbar('Barkod kopyalanırken bir hata oluştu', 'error');
    }
  };

  const handleEditClick = (product: PendingProduct) => {
    setEditDialog({ open: true, product });
    setEditUrunAdi(product.urun_adi);
    setEditBarkod(product.barkod || '');
  };

  const handleApproveProduct = async () => {
    if (!editDialog.product?.id) return;

    setApproving(true);

    try {
      await ProductService.approvePendingProduct(editDialog.product.id, {
        urun_adi: editUrunAdi,
        barkod: editBarkod,
      });

      const updated = productList.filter((p) => p.id !== editDialog.product!.id);
      dataCache.set(CACHE_KEYS.pendingProducts, updated);
      const newTotalPages = Math.max(1, Math.ceil(updated.length / PAGE_SIZE));
      if (page > newTotalPages) setPage(newTotalPages);
      await refetch(false);

      setEditDialog({ open: false });
      showSnackbar('Ürün eklendi ve bekleyen listeden kaldırıldı', 'success');
    } catch (error) {
      console.error('Ürün onaylanırken hata:', error);
      const message = error instanceof ProductError
        ? error.message
        : 'Ürün eklenirken bir hata oluştu';
      showSnackbar(message, 'error');
    } finally {
      setApproving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && !products) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Bekleyen Ürünler"
        subtitle={
          productList.length > 0
            ? `${productList.length} ürün onay bekliyor · Sayfa ${page}/${totalPages}`
            : 'Kullanıcılar tarafından eklenen ve onay bekleyen ürünler'
        }
        icon={<HourglassEmptyIcon />}
        actions={
          <Tooltip title="Listeyi yenile">
            <span>
              <IconButton onClick={() => refetch(true)} disabled={loading} size="small">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      {productList.length === 0 ? (
        <EmptyState
          icon={<HourglassEmptyIcon sx={{ fontSize: 36 }} />}
          title="Bekleyen ürün yok"
          description="Şu anda onay bekleyen ürün bulunmuyor. Yeni ürünler eklendiğinde burada görünecek."
        />
      ) : (
        <DataTableCard subtitle={`Sayfa ${page} — ${paginatedProducts.length} kayıt gösteriliyor`}>
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Ürün Adı</TableCell>
                  <TableCell>Son Kullanma</TableCell>
                  <TableCell>Barkod</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: stringToColor(product.addedBy || 'User'),
                          }}
                        >
                          {getInitials(product.addedBy || 'User')}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {product.addedBy || 'Bilinmiyor'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {product.urun_adi}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {product.expirationDate
                          ? new Date(product.expirationDate).toLocaleDateString('tr-TR')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title={product.barkod ? 'Kopyalamak için tıklayın' : ''}>
                          <Chip
                            label={product.barkod || 'Barkod Yok'}
                            size="small"
                            variant="outlined"
                            onClick={() => product.barkod && handleCopyBarkod(product.barkod)}
                            sx={{ cursor: product.barkod ? 'pointer' : 'default' }}
                          />
                        </Tooltip>
                        {product.barkod && (
                          <Tooltip title="Barkodu Kopyala">
                            <IconButton size="small" onClick={() => handleCopyBarkod(product.barkod!)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{product.categoryId || '—'}</TableCell>
                    <TableCell>
                      <Chip label={product.status || 'Beklemede'} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Düzenle ve ekle">
                        <IconButton size="small" color="primary" onClick={() => handleEditClick(product)}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(product.id, product.urun_adi)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DataTableCard>
      )}

      {productList.length > PAGE_SIZE && (
        <Box
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          justifyContent="center"
          alignItems="center"
          mt={4}
          gap={2}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
            <TextField
              size="small"
              label="Sayfaya Git"
              value={gotoPage}
              onChange={(e) => setGotoPage(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={handleGotoInputKeyDown}
              error={!!gotoError}
              helperText={gotoError}
              sx={{ width: 120 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, max: totalPages }}
            />
            <IconButton color="primary" onClick={handleGotoPage} aria-label="Git" sx={{ mt: 0.5 }}>
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
        </Box>
      )}

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Ürünü Sil</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            <strong>{deleteDialog.productName}</strong> ürününü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialog({ open: false })} color="inherit">
            İptal
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Ürünü Düzenle ve Products'a Ekle</DialogTitle>
        <DialogContent>
          <TextField
            label="Ürün Adı"
            value={editUrunAdi}
            onChange={(e) => setEditUrunAdi(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Barkod"
            value={editBarkod}
            onChange={(e) => setEditBarkod(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditDialog({ open: false })} color="inherit">
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveProduct}
            disabled={approving || !editUrunAdi.trim() || !editBarkod.trim()}
          >
            {approving ? 'Ekleniyor...' : "Products'a Ekle"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

function stringToColor(string: string): string {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

function getInitials(name: string): string {
  return name
    .split('@')[0]
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() || '')
    .join('');
}

export default PendingProducts;
